"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createSmsSettings,
  deleteSmsSettings,
  getSmsSettings,
  updateSmsSettings,
} from "@/actions/integrations/smsSettings";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import { useAlert } from "@/hooks/useAlert";
import {
  ConversationTTL,
  SmsProviderSettings,
  SmsSettings,
  SmsSettingsRequest,
} from "@/types/settings";
import { extractApiErrorDetail } from "@/utils/integrations/api-errors";
import DeleteSmsSettingsDialog from "./DeleteSmsSettingsDialog";
import SmsProviderForm from "./SmsProviderForm";

type LoadState = "loading" | "error" | "forbidden" | "ready";

interface SmsSettingsPanelProps {
  /** Called after the settings are created, updated or deleted. */
  onChanged?: () => void;
}

interface SummaryRow {
  /** Locale-independent React key — labels are translated and can collide. */
  id: string;
  label: string;
  value: string;
  /** Secret values are never shown — this renders the "Saved" placeholder. */
  isSecret?: boolean;
}

function summaryRows(
  provider: SmsProviderSettings,
  t: (key: string) => string
): SummaryRow[] {
  const secretValue = t("smsIntegration.summary.secretStored");
  switch (provider.provider) {
    case "twilio":
      return [
        {
          id: "phoneNumber",
          label: t("smsIntegration.summary.phoneNumber"),
          value: provider.phoneNumber ?? "",
        },
        ...(provider.accountSid
          ? [
              {
                id: "accountSid",
                label: t("smsIntegration.summary.accountSid"),
                value: provider.accountSid,
              },
            ]
          : []),
        ...(provider.messagingServiceSid
          ? [
              {
                id: "messagingServiceSid",
                label: t("smsIntegration.summary.messagingServiceSid"),
                value: provider.messagingServiceSid,
              },
            ]
          : []),
        {
          id: "authToken",
          label: t("smsIntegration.summary.authToken"),
          value: secretValue,
          isSecret: true,
        },
      ];
    case "quickly":
      return [
        { id: "url", label: t("smsIntegration.summary.url"), value: provider.url ?? "" },
        {
          id: "apiKey",
          label: t("smsIntegration.summary.apiKey"),
          value: secretValue,
          isSecret: true,
        },
        {
          id: "apiSecret",
          label: t("smsIntegration.summary.apiSecret"),
          value: secretValue,
          isSecret: true,
        },
      ];
    case "directo":
      return [
        {
          id: "fromPhoneNumber",
          label: t("smsIntegration.summary.fromPhoneNumber"),
          value: provider.fromPhoneNumber ?? "",
        },
      ];
  }
}

export default function SmsSettingsPanel({ onChanged }: SmsSettingsPanelProps) {
  const { t } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const { setAlert, setIsSaving } = useAlert();

  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [settings, setSettings] = useState<SmsSettings | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const { success, status, data } = await getSmsSettings();
      if (success && data) {
        setSettings(data);
        setLoadState("ready");
        return;
      }
      if (status === 404) {
        setSettings(null);
        setLoadState("ready");
        return;
      }
      if (status === 403) {
        setLoadState("forbidden");
        return;
      }
      setLoadState("error");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buildPayload = (provider: SmsProviderSettings): SmsSettingsRequest => ({
    provider,
    // TTL and idle reminder are round-tripped unchanged: the conversation
    // lifecycle still reads them from Organization settings, so this UI does
    // not expose them but must not reset values set via the API.
    smsConversationTtl: settings?.smsConversationTtl ?? ConversationTTL.AllTime,
    smsIdleReminder: settings?.smsIdleReminder ?? { enabled: false },
  });

  const handleSave = async (provider: SmsProviderSettings) => {
    setIsSaving(true, ct("saving"));
    try {
      const payload = buildPayload(provider);
      const result = settings
        ? await updateSmsSettings(settings.id, payload)
        : await createSmsSettings(payload);

      if (result.success && result.data) {
        setSettings(result.data);
        setEditing(false);
        setAlert({
          status: "success",
          message: t("smsIntegration.saveSuccess"),
          secondsToClose: 4,
        });
        onChanged?.();
        return;
      }
      if (result.status === 403) {
        setAlert({
          status: "error",
          message: t("smsIntegration.forbidden"),
          secondsToClose: 10,
        });
        return;
      }
      if (result.status === 409) {
        setAlert({
          status: "error",
          message: t("smsIntegration.alreadyExists"),
          secondsToClose: 10,
        });
        await load();
        setEditing(false);
        onChanged?.();
        return;
      }
      setAlert({
        status: "error",
        message: extractApiErrorDetail(result.data) ?? t("smsIntegration.saveError"),
        secondsToClose: 10,
      });
    } finally {
      setIsSaving(false, "");
    }
  };

  const handleDelete = async () => {
    if (!settings) return;
    setIsSaving(true, ct("saving"));
    try {
      const { success, status } = await deleteSmsSettings(settings.id);
      if (success) {
        setSettings(null);
        setConfirmingDelete(false);
        setAlert({
          status: "success",
          message: t("smsIntegration.deleteSuccess"),
          secondsToClose: 4,
        });
        onChanged?.();
        return;
      }
      setAlert({
        status: "error",
        message: t(
          status === 403 ? "smsIntegration.forbidden" : "smsIntegration.deleteError"
        ),
        secondsToClose: 10,
      });
    } finally {
      setIsSaving(false, "");
    }
  };

  if (loadState === "loading") {
    return <div className="p-4">{ct("loading")}...</div>;
  }

  if (loadState === "error") {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-600">{t("smsIntegration.loadError")}</p>
        <Button type="secondary" onClick={load}>
          {ct("retry")}
        </Button>
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div>
        <p className="text-sm text-600">{t("smsIntegration.forbidden")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!settings && !editing && (
        <div>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-200">
              <Icon icon="message" className="text-800" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-800">{t("smsIntegration.empty.title")}</h3>
              <p className="text-sm text-500 mt-1">{t("smsIntegration.empty.description")}</p>
              <div className="mt-4">
                <Button type="primary" onClick={() => setEditing(true)}>
                  {t("smsIntegration.empty.configure")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <SmsProviderForm
          initialProvider={settings?.provider ?? null}
          onSubmit={handleSave}
          onCancel={settings ? () => setEditing(false) : undefined}
        />
      )}

      {settings && !editing && (
        <div>
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-200">
              <Icon icon="message" className="text-800" />
            </div>
            <div>
              <h3 className="font-medium text-800">{t("smsIntegration.summary.title")}</h3>
              <p className="text-sm text-500">
                {t(`smsIntegration.provider.${settings.provider.provider}`)}
              </p>
            </div>
          </div>

          <dl className="space-y-3">
            {summaryRows(settings.provider, t).map((row) => (
              <div key={row.id} className="flex justify-between gap-4">
                <dt className="text-sm text-500">{row.label}</dt>
                <dd
                  className={
                    row.isSecret
                      ? "text-sm text-600"
                      : "text-sm text-800 font-mono break-all"
                  }
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="secondary" destructive onClick={() => setConfirmingDelete(true)}>
              {t("smsIntegration.summary.delete")}
            </Button>
            <Button type="primary" onClick={() => setEditing(true)}>
              {ct("edit")}
            </Button>
          </div>
        </div>
      )}

      <DeleteSmsSettingsDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        onConfirm={handleDelete}
      />
    </div>
  );
}
